import React, { useEffect, useState, useMemo, useCallback } from "react";
import Button from "./../../ui/Button";
import UserTransferService from "./../../../services/UserTransferService";
import {
  getRequesterHighlightStyleByIndex,
  getTransferRequester,
} from "./../../../utils/requesterHighlight";
import "./../../../styles/BulkDeviceTransferModal.css";

const getTransferKey = (transfer) => {
  return transfer.id ?? transfer.visorId ?? `${transfer.groupId ?? ""}-${transfer.requestDate ?? ""}`;
};

export default function BulkDeviceTransferModal({
  isOpen,
  onClose,
  sites = [],
  siteId = 1,
}) {
  const [pendingTransfers, setPendingTransfers] = useState([]);
  const [isFetching, setIsFetching] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [perRowGroupIds, setPerRowGroupIds] = useState({});
  const [rowStatuses, setRowStatuses] = useState({});
  const [filterSourceGroup, setFilterSourceGroup] = useState("");
  const [newGroupId, setNewGroupId] = useState("");
  const [showSubmitConfirm, setShowSubmitConfirm] = useState(false);

  const siteOptions = useMemo(() => {
    return (sites || [])
      .map((s) => ({
        label: s?.sitename || `#${s?.id ?? ""}`,
        value: String(s?.id ?? ""),
        level: s?.level || "",
      }))
      .filter((s) => s.value && s.value !== "1")
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [sites]);

  const sourceGroups = useMemo(() => {
    const unique = [...new Set(pendingTransfers.map((t) => t.groupName).filter(Boolean))];
    return unique.sort((a, b) => a.localeCompare(b));
  }, [pendingTransfers]);

  const filteredTransfers = useMemo(() => {
    if (!filterSourceGroup) return pendingTransfers;
    return pendingTransfers.filter((t) => t.groupName === filterSourceGroup);
  }, [pendingTransfers, filterSourceGroup]);

  const selectedDestination = useMemo(() => {
    if (!newGroupId) return null;
    return siteOptions.find((site) => String(site.value) === String(newGroupId)) || null;
  }, [siteOptions, newGroupId]);

  const transfersReadyToSubmit = useMemo(() => {
    return filteredTransfers.filter((transfer) => {
      return Boolean(perRowGroupIds[getTransferKey(transfer)] || newGroupId);
    });
  }, [filteredTransfers, perRowGroupIds, newGroupId]);

  const requesterHighlightStyles = useMemo(() => {
    const stylesByRequester = {};
    let nextIndex = 0;

    pendingTransfers.forEach((transfer) => {
      const requester = getTransferRequester(transfer).trim();
      const requesterKey = requester.toLowerCase();

      if (!stylesByRequester[requesterKey]) {
        stylesByRequester[requesterKey] = getRequesterHighlightStyleByIndex(nextIndex);
        nextIndex += 1;
      }
    });

    return stylesByRequester;
  }, [pendingTransfers]);

  const canSubmit = useMemo(() => {
    if (isFetching || isSubmitting) return false;
    if (filteredTransfers.length === 0) return false;

    return transfersReadyToSubmit.length > 0;
  }, [filteredTransfers.length, transfersReadyToSubmit.length, isFetching, isSubmitting]);

  const resetState = useCallback(() => {
    setPendingTransfers([]);
    setIsFetching(false);
    setIsSubmitting(false);
    setError("");
    setSuccess("");
    setPerRowGroupIds({});
    setRowStatuses({});
    setFilterSourceGroup("");
    setNewGroupId("");
    setShowSubmitConfirm(false);
  }, []);

  const fetchPendingTransfers = useCallback(async () => {
    setIsFetching(true);
    setError("");
    setShowSubmitConfirm(false);
    setRowStatuses({});
    setPendingTransfers([]);

    try {
      const response = await UserTransferService.getAllTransfers(siteId);

      const data = Array.isArray(response)
        ? response
        : Array.isArray(response?.data)
        ? response.data
        : Array.isArray(response?.data?.data)
        ? response.data.data
        : [];

      const pendingOnly = data.filter(
        (t) =>
          t.status !== "actioned" &&
          t.status !== "completed" &&
          t.status !== "processed" &&
          !t.isProcessed &&
          !t.completed
      );

      setPendingTransfers(pendingOnly);

      const initialSelections = {};
      const initialStatuses = {};

      pendingOnly.forEach((t) => {
        const transferKey = getTransferKey(t);
        initialSelections[transferKey] = "";
        initialStatuses[transferKey] = { status: "idle", message: "" };
      });

      setPerRowGroupIds(initialSelections);
      setRowStatuses(initialStatuses);

      if (pendingOnly.length === 0) {
        setError("No pending transfer requests were found for this site.");
      }
    } catch (err) {
      setError(
        "No pending transfer requests were found. Please refresh the page or sign in again."
      );
    } finally {
      setIsFetching(false);
    }
  }, [siteId]);

  useEffect(() => {
    if (!isOpen) return;

    resetState();
    fetchPendingTransfers();
  }, [isOpen, resetState, fetchPendingTransfers]);

  const handleApplyToAll = useCallback(() => {
    if (!newGroupId) return;
    setShowSubmitConfirm(false);

    setPerRowGroupIds((prev) => {
      const next = { ...prev };

      filteredTransfers.forEach((t) => {
        const transferKey = getTransferKey(t);
        const currentSiteId = String(t.groupId ?? t.currentGroupId ?? "");
        const destinationSiteId = String(newGroupId);

        if (currentSiteId && currentSiteId === destinationSiteId) {
          return;
        }

        if (rowStatuses[transferKey]?.status === "success") {
          return;
        }

        next[transferKey] = destinationSiteId;
      });

      return next;
    });
  }, [filteredTransfers, newGroupId, rowStatuses]);

  const submit = useCallback(async () => {
    setError("");
    setSuccess("");
    setShowSubmitConfirm(false);

    const transfersToProcess = filteredTransfers
      .map((t) => {
        const transferKey = getTransferKey(t);
        const selectedGroupId = perRowGroupIds[transferKey] || newGroupId;

        return {
          id: transferKey,
          visorId: t.visorId,
          currentGroupId: String(t.groupId ?? t.currentGroupId ?? ""),
          selectedGroupId,
          targetGroupId: Number(selectedGroupId),
        };
      })
      .filter((t) => t.selectedGroupId !== "" && t.selectedGroupId !== undefined && t.selectedGroupId !== null);

    if (transfersToProcess.length === 0) {
      setError("Please select a destination site for at least one visor before submitting.");
      return;
    }

    const invalidSameSite = transfersToProcess.filter(
      (t) => t.currentGroupId && t.currentGroupId === String(t.targetGroupId)
    );

    if (invalidSameSite.length > 0) {
      setError("One or more rows have the same current site and destination site.");
      return;
    }

    setIsSubmitting(true);

    let successCount = 0;
    const failures = [];

    for (const item of transfersToProcess) {
      try {
        setRowStatuses((prev) => ({
          ...prev,
          [item.id]: { status: "processing", message: "" },
        }));

        await UserTransferService.actionTransfer({
          visorRef: item.visorId,
          newGroupId: item.targetGroupId,
        });

        successCount += 1;

        setRowStatuses((prev) => ({
          ...prev,
          [item.id]: { status: "success", message: "Transfer completed" },
        }));
      } catch (err) {
        const apiMessage =
          err?.response?.data?.message || err?.message || "Unknown error";

        failures.push({
          id: item.id,
          visorId: item.visorId,
          message: apiMessage,
        });

        setRowStatuses((prev) => ({
          ...prev,
          [item.id]: { status: "error", message: apiMessage },
        }));
      }
    }

    setIsSubmitting(false);

    if (successCount > 0 && failures.length === 0) {
      setSuccess(`Successfully submitted ${successCount} transfer${successCount === 1 ? "" : "s"}.`);
    }

    if (successCount > 0 && failures.length > 0) {
      setSuccess(`Submitted ${successCount} transfer${successCount === 1 ? "" : "s"}.`);
      setError(`${failures.length} transfer${failures.length === 1 ? "" : "s"} failed: ${failures.map(f => f.visorId).join(", ")}`);
    }

    if (successCount === 0 && failures.length > 0) {
      setError(`All transfers failed: ${failures.map(f => f.visorId).join(", ")}`);
    }

    await fetchPendingTransfers();
  }, [filteredTransfers, perRowGroupIds, newGroupId, fetchPendingTransfers]);

  const handleSubmitClick = useCallback(() => {
    setError("");
    setSuccess("");
    setShowSubmitConfirm(true);
  }, []);

  if (!isOpen) return null;

  return (
    <div className="sd-modal-backdrop" role="dialog" aria-modal="true">
      <div className="sd-modal sd-modal-md bdtm-modal">
        <div className="sd-modal-header">
          <div>
            <h2 className="sd-title">Bulk Device Transfer</h2>
            <div className="sd-subtitle">Link devices to a new destination site.</div>
          </div>
          <button
            className="sd-icon-btn"
            onClick={onClose}
            aria-label="Close"
            type="button"
            disabled={isSubmitting}
          >
            ✕
          </button>
        </div>

        <div className="sd-modal-body">
          {error && <div className="sd-alert sd-alert-info">{error}</div>}
          {success && <div className="sd-alert sd-alert-good">{success}</div>}

          {sourceGroups.length > 0 && (
            <div className="sd-field">
              <div className="sd-label">Pending Transfers Group</div>
              <select
                className="sd-input"
                value={filterSourceGroup}
                onChange={(e) => {
                  setFilterSourceGroup(e.target.value);
                  setShowSubmitConfirm(false);
                }}
                disabled={isFetching || isSubmitting}
              >
                <option value="">-- All Groups --</option>
                {sourceGroups.map((group) => (
                  <option key={group} value={group}>
                    {group}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="sd-field">
            <div className="sd-label">Optional bulk destination site</div>

            <select
              className="sd-input bdtm-destination-select"
              value={newGroupId}
              onChange={(e) => {
                setShowSubmitConfirm(false);
                setNewGroupId(e.target.value);
              }}
              disabled={isFetching || isSubmitting}
            >
              <option value="">-- Select destination site --</option>
              {siteOptions.map((site) => (
                <option key={site.value} value={site.value}>
                  {site.label}{site.level ? ` (${site.level})` : ""}
                </option>
              ))}
            </select>
          </div>

          {selectedDestination && (
            <div className="bdtm-destination-summary is-selected" aria-live="polite">
              <div>
                <div className="bdtm-summary-kicker">Please check before applying</div>
                <div className="bdtm-summary-name">
                  Are you sure you want to transfer users to {selectedDestination.label}?
                </div>
                <div className="bdtm-summary-warning">
                  Transferring users to {selectedDestination.label} will also move their
                  associated data to that destination site.
                </div>
              </div>
              <div className="bdtm-summary-meta">
                {selectedDestination.level || `Site #${selectedDestination.value}`}
              </div>
            </div>
          )}

          <div className="std-button">
            <Button
              type="button"
              buttonStyle="btn-alternate"
              onClick={handleApplyToAll}
              disabled={!newGroupId || isFetching || isSubmitting}
            >
              Apply destination to all visible transfers
            </Button>
          </div>

          {isFetching && <div>Loading...</div>}

          {!isFetching && filteredTransfers.length === 0 && !error && (
            <div>No pending transfers to show.</div>
          )}

          <div className="sd-transfer-table bdtm-table">
            <div className="sd-transfer-head bdtm-head">
              <div>Visor Reference</div>
              <div>Requester</div>
              <div>Current Site</div>
              <div>Destination Site</div>
              <div>Status</div>
              <div>Message</div>
            </div>

            {filteredTransfers.map((transfer) => {
              const transferKey = getTransferKey(transfer);
              const requester = getTransferRequester(transfer);
              const requesterKey = requester.trim().toLowerCase();
              const rowState = rowStatuses[transferKey] || {
                status: "idle",
                message: "",
              };

              return (
                <div
                  className="sd-transfer-row bdtm-row bdtm-requester-highlight-row"
                  key={transferKey}
                  style={requesterHighlightStyles[requesterKey]}
                >
                  <div className="sd-cell sd-strong">{transfer.visorId || "-"}</div>
                  <div className="sd-cell bdtm-requester-cell">{requester}</div>
                  <div className="sd-cell">{transfer.groupName || "-"}</div>

                  <div className="sd-cell">
                    <select
                      className={`sd-input bdtm-row-destination ${
                        perRowGroupIds[transferKey] ? "has-selection" : ""
                      }`}
                      value={perRowGroupIds[transferKey] ?? ""}
                      onChange={(e) => {
                        setShowSubmitConfirm(false);
                        setPerRowGroupIds((prev) => ({
                          ...prev,
                          [transferKey]: e.target.value,
                        }));
                      }}
                      disabled={isFetching || isSubmitting}
                    >
                      <option value="">-- Select destination site --</option>
                      {siteOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="sd-cell">
                    {rowState.status === "processing" && (
                      <span className="status-spin">⏳ Processing</span>
                    )}
                    {rowState.status === "success" && (
                      <span style={{ color: "green" }}>✅ Success</span>
                    )}
                    {rowState.status === "error" && (
                      <span style={{ color: "red" }}>❌ Failed</span>
                    )}
                    {rowState.status === "idle" && <span>Idle</span>}
                  </div>

                  <div className="sd-cell">{rowState.message || ""}</div>
                </div>
              );
            })}
          </div>

        </div>

        {showSubmitConfirm && (
          <div className="bdtm-submit-confirm" role="alertdialog" aria-live="assertive">
            <div>
              <div className="bdtm-summary-kicker">Confirm transfer</div>
              <div className="bdtm-summary-name">Are you sure?</div>
              <div className="bdtm-summary-warning">
                You are about to transfer {transfersReadyToSubmit.length} selected
                visor{transfersReadyToSubmit.length === 1 ? "" : "s"}. This will move each
                selected user and their associated data to the destination site chosen
                in that row.
              </div>
            </div>
            <div className="bdtm-confirm-actions">
              <button
                type="button"
                className="bdtm-confirm-btn bdtm-confirm-btn-secondary"
                onClick={() => setShowSubmitConfirm(false)}
                disabled={isSubmitting}
              >
                Review again
              </button>
              <button
                type="button"
                className="bdtm-confirm-btn bdtm-confirm-btn-primary"
                onClick={submit}
                disabled={isSubmitting}
              >
                {isSubmitting ? "Submitting..." : "Yes, submit transfers"}
              </button>
            </div>
          </div>
        )}

        <div className="sd-modal-footer">
          <Button
            type="button"
            buttonStyle="btn-alternate"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>

          {!showSubmitConfirm && (
            <Button
              type="button"
              buttonStyle="btn-main"
              onClick={handleSubmitClick}
              disabled={!canSubmit}
            >
              {isSubmitting ? "Submitting..." : "Submit Transfers"}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
